import React, { useCallback } from 'react';
import { AdvancedMarker, AdvancedMarkerAnchorPoint, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import colors from 'assets/scss/colors.module.scss';

// Base marker types
export type MarkerPosition = { lat: number; lng: number };
export type MarkerClickHandler<T = any> = (data: T) => void;

// SVG Components for different marker styles
interface IMarkerSVG {
	color?: string;
	size?: number;
	opacity?: number;
}

export const CircleMarkerSVG: React.FC<IMarkerSVG> = ({ 
	color = colors.primary, 
	size = 20, 
	opacity = 0.3 
}) => (
	<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx={size/2} cy={size/2} r={size/2} fill={color} fillOpacity={opacity} />
	</svg>
);

export const PinMarkerSVG: React.FC<IMarkerSVG> = ({ 
	color = colors.primary, 
	size = 22,
	opacity = 0.8 
}) => {
	const width = size;
	const height = Math.floor(size * 1.36); // Maintain aspect ratio
	
	return (
		<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d={`M${width * 0.448} ${height * 0.978}C${width * 0.0677} ${height * 0.569} 0 ${height * 0.527} 0 ${height * 0.375}C0 ${height * 0.168} ${width * 0.221} 0 ${width * 0.5} 0C${width * 0.774} 0 ${width} ${height * 0.168} ${width} ${height * 0.375}C${width} ${height * 0.527} ${width * 0.930} ${height * 0.569} ${width * 0.549} ${height * 0.978}C${width * 0.532} ${height * 1.007} ${width * 0.486} ${height * 1.007} ${width * 0.448} ${height * 0.978}Z`}
				fill={color}
				fillOpacity={opacity}
			/>
			<circle cx={width/2} cy={height * 0.37} r={width * 0.36} fill="black" fillOpacity="0.3" />
		</svg>
	);
};

interface IClusterMarkerSVG extends IMarkerSVG {
	text?: string;
}

export const AgtMarkerSVG: React.FC<IMarkerSVG> = ({ 
	color = colors.primary, 
	size = 40,
	opacity = 0.7 
}) => (
	<svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="20" cy="20" r="20" fill={color} fillOpacity={opacity} />
		<path
			d="M16.5714 16.2031V14.7969C16.5714 14.5859 16.75 14.375 17 14.375H18.4286C18.6429 14.375 18.8571 14.5859 18.8571 14.7969V16.2031C18.8571 16.4492 18.6429 16.625 18.4286 16.625H17C16.75 16.625 16.5714 16.4492 16.5714 16.2031ZM21.5714 16.625C21.3214 16.625 21.1429 16.4492 21.1429 16.2031V14.7969C21.1429 14.5859 21.3214 14.375 21.5714 14.375H23C23.2143 14.375 23.4286 14.5859 23.4286 14.7969V16.2031C23.4286 16.4492 23.2143 16.625 23 16.625H21.5714ZM17 20C16.75 20 16.5714 19.8242 16.5714 19.5781V18.1719C16.5714 17.9609 16.75 17.75 17 17.75H18.4286C18.6429 17.75 18.8571 17.9609 18.8571 18.1719V19.5781C18.8571 19.8242 18.6429 20 18.4286 20H17ZM21.5714 20C21.3214 20 21.1429 19.8242 21.1429 19.5781V18.1719C21.1429 17.9609 21.3214 17.75 21.5714 17.75H23C23.2143 17.75 23.4286 17.9609 23.4286 18.1719V19.5781C23.4286 19.8242 23.2143 20 23 20H21.5714ZM18.8571 22.9531C18.8571 23.1992 18.6429 23.375 18.4286 23.375H17C16.75 23.375 16.5714 23.1992 16.5714 22.9531V21.5469C16.5714 21.3359 16.75 21.125 17 21.125H18.4286C18.6429 21.125 18.8571 21.3359 18.8571 21.5469V22.9531ZM21.5714 23.375C21.3214 23.375 21.1429 23.1992 21.1429 22.9531V21.5469C21.1429 21.3359 21.3214 21.125 21.5714 21.125H23C23.2143 21.125 23.4286 21.3359 23.4286 21.5469V22.9531C23.4286 23.1992 23.2143 23.375 23 23.375H21.5714ZM28 27.7344V29H12V27.7344C12 27.5234 12.1786 27.3125 12.4286 27.3125H13.1071V11.8438C13.1071 11.3867 13.5 11 13.9643 11H26C26.4643 11 26.8571 11.3867 26.8571 11.8438V27.3125H27.5714C27.7857 27.3125 28 27.5234 28 27.7344ZM14.8214 27.2773H18.8571V24.9219C18.8571 24.7109 19.0357 24.5 19.2857 24.5H20.7143C20.9286 24.5 21.1429 24.7109 21.1429 24.9219V27.2773H25.1429V12.7227L14.8571 12.6875L14.8214 27.2773Z"
			fill="white"
		/>
	</svg>
);

export const ClusterMarkerSVG: React.FC<IClusterMarkerSVG> = ({ 
	color = colors.primary, 
	size = 100, 
	text = "" 
}) => {
	const r = size / 3; // make smaller so we don't extend the bounds of the SVG when animating
	const num = 3; // number of ripple circles
	
	return (
		<svg className="marker-svg" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
			{/* Animated ripple circles */}
			{Array.from({ length: num }).map((_, i) => {
				return (
					<circle 
						key={i} 
						className={`c-${i + 1}`} 
						cx={size / 2} 
						cy={size / 2} 
						r={r} 
						fill={color} 
						fillOpacity={0}
					>
						<animate attributeName="r" begin={`${i}s`} dur="3s" to={size / 2} repeatCount="indefinite" />
						<animate attributeName="fill-opacity" begin={`${i}s`} dur="3s" values="0;0.5;0" repeatCount="indefinite" />
					</circle>
				);
			})}
			{/* Text with drop shadow filter */}
			{text && (
				<>
					<defs>
						<filter id={`text-filter-${size}`}>
							<feDropShadow dx="0" dy="0" stdDeviation="1" floodColor={color} floodOpacity="1" />
							<feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={color} floodOpacity="1" />
						</filter>
					</defs>
					<text 
						filter={`url(#text-filter-${size})`} 
						x="50%" 
						y="50%" 
						dominantBaseline="central" 
						textAnchor="middle" 
						fontSize={12} 
						fill="white"
					>
						{text}
					</text>
				</>
			)}
		</svg>
	);
};

// Base Marker Component
export interface IBaseMarker {
	position: MarkerPosition;
	title?: string;
	className?: string;
	zIndex?: number;
	anchorPoint?: any;
	onClick?: () => void;
	children: React.ReactNode;
}

export const BaseMarker: React.FC<IBaseMarker> = ({
	position,
	title,
	className = 'marker',
	zIndex,
	anchorPoint = AdvancedMarkerAnchorPoint.CENTER,
	onClick,
	children
}) => {
	return (
		<AdvancedMarker
			position={position}
			title={title}
			className={className}
			zIndex={zIndex}
			anchorPoint={anchorPoint}
			onClick={onClick}
		>
			{children}
		</AdvancedMarker>
	);
};

// Simple Marker (Circle style)
export interface ISimpleMarker {
	position: MarkerPosition;
	title?: string;
	color?: string;
	size?: number;
	onClick?: () => void;
	className?: string;
}

export const SimpleMarker: React.FC<ISimpleMarker> = ({
	position,
	title,
	color,
	size = 20,
	onClick,
	className = 'marker simple'
}) => {
	return (
		<BaseMarker
			position={position}
			title={title}
			className={className}
			onClick={onClick}
		>
			<CircleMarkerSVG color={color} size={size} />
		</BaseMarker>
	);
};

// Agency Marker (Pin style)
export interface IAgenturMarker {
	position: MarkerPosition;
	title?: string;
	color?: string;
	size?: number;
	onClick?: () => void;
	className?: string;
}

export const AgenturMarker: React.FC<IAgenturMarker> = ({
	position,
	title,
	color,
	size = 22,
	onClick,
	className = 'marker feature'
}) => {
	return (
		<BaseMarker
			position={position}
			title={title}
			className={className}
			onClick={onClick}
		>
			<PinMarkerSVG color={color} size={size} />
		</BaseMarker>
	);
};

// Cluster Marker
export interface IClusterMarker {
	position: MarkerPosition;
	size: number;
	count: number;
	color?: string;
	onClick?: () => void;
	className?: string;
	zIndex?: number;
}

export const ClusterMarker: React.FC<IClusterMarker> = ({
	position,
	size,
	count,
	color,
	onClick,
	className = 'marker cluster',
	zIndex
}) => {
	const markerSize = Math.floor(80 + Math.sqrt(size) * 2);
	
	return (
		<BaseMarker
			position={position}
			className={className}
			zIndex={zIndex || size}
			onClick={onClick}
		>
			<ClusterMarkerSVG color={color} size={markerSize} text={count.toString()} />
		</BaseMarker>
	);
};

// Advanced Marker with ref support (for when you need the marker element)
export interface IAdvancedMarker {
	position: MarkerPosition;
	title?: string;
	className?: string;
	zIndex?: number;
	onMarkerClick?: (marker: google.maps.marker.AdvancedMarkerElement) => void;
	children: React.ReactNode;
}

export const AdvancedMarkerWithRef: React.FC<IAdvancedMarker> = ({
	position,
	title,
	className = 'marker',
	zIndex,
	onMarkerClick,
	children
}) => {
	const [markerRef, marker] = useAdvancedMarkerRef();
	
	const handleClick = useCallback(() => {
		if (onMarkerClick && marker) {
			onMarkerClick(marker);
		}
	}, [onMarkerClick, marker]);

	return (
		<AdvancedMarker
			ref={markerRef}
			position={position}
			title={title}
			className={className}
			zIndex={zIndex}
			onClick={handleClick}
			anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
		>
			{children}
		</AdvancedMarker>
	);
};

// Specialized markers that use the base components
export interface IAgenturModelMarker {
	agt: {
		lat?: number;
		lng?: number;
		name?: string;
		agtId: number;
	};
	color?: string;
	size?: number;
	onClick?: (agt: any) => void;
}

export const AgenturModelMarker: React.FC<IAgenturModelMarker> = ({
	agt,
	color,
	size,
	onClick
}) => {
	if (!agt || !agt.lat || !agt.lng || !agt.name) {
		return null;
	}

	const handleClick = () => {
		if (onClick) {
			onClick(agt);
		}
	};

	return (
		<AgenturMarker
			position={{ lat: agt.lat, lng: agt.lng }}
			title={agt.name}
			color={color}
			size={size}
			onClick={handleClick}
		/>
	);
};

// Feature marker for clustering
export interface IFeatureMarker {
	position: MarkerPosition;
	featureId: string;
	onMarkerClick?: (marker: google.maps.marker.AdvancedMarkerElement, featureId: string) => void;
}

export const FeatureMarker: React.FC<IFeatureMarker> = ({
	position,
	featureId,
	onMarkerClick
}) => {
	const handleMarkerClick = useCallback((marker: google.maps.marker.AdvancedMarkerElement) => {
		if (onMarkerClick) {
			onMarkerClick(marker, featureId);
		}
	}, [onMarkerClick, featureId]);

	return (
		<AdvancedMarkerWithRef
			position={position}
			className="marker feature"
			onMarkerClick={handleMarkerClick}
		>
			<AgtMarkerSVG />
		</AdvancedMarkerWithRef>
	);
};

// Cluster marker for clustering
export interface IClusterFeatureMarker {
	position: MarkerPosition;
	clusterId: number;
	size: number;
	onMarkerClick?: (marker: google.maps.marker.AdvancedMarkerElement, clusterId: number) => void;
}

export const ClusterFeatureMarker: React.FC<IClusterFeatureMarker> = ({
	position,
	clusterId,
	size,
	onMarkerClick
}) => {
	const handleMarkerClick = useCallback((marker: google.maps.marker.AdvancedMarkerElement) => {
		if (onMarkerClick) {
			onMarkerClick(marker, clusterId);
		}
	}, [onMarkerClick, clusterId]);

	return (
		<AdvancedMarkerWithRef
			position={position}
			className="marker cluster"
			zIndex={size}
			onMarkerClick={handleMarkerClick}
		>
			<ClusterMarkerSVG size={Math.floor(80 + Math.sqrt(size) * 2)} text={size.toString()} />
		</AdvancedMarkerWithRef>
	);
};